import { hasOwnPropertySafe, checkType } from './helpers';
import {
	MANDATORY_SERVICE_KEYS,
	SERVICE_KEY_TYPES,
	ENGINE_KEY_TYPES,
	MANDATORY_FP_CONFIG_KEYS
} from '../pages/BannerFpConfigGenerator/constants';

export const checkBannerFpEngineConfig = (data) => {
	const {
		bannerFpConfig,
		ALL_ENGINE_MODELS,
		ALL_SERVICES,
		CONFIG_STRUCTURE_KEYS,
		SERVICE_CONFIG_KEYS,
		MANUAL_FLOOR_KEYS
	} = data;

	if (
		!bannerFpConfig ||
		!ALL_ENGINE_MODELS ||
		!ALL_SERVICES ||
		!CONFIG_STRUCTURE_KEYS ||
		!SERVICE_CONFIG_KEYS ||
		!MANUAL_FLOOR_KEYS
	) {
		throw new Error('All Details not passed to checkBannerFpEngineConfig function!');
	}

	checkRootLevelFeatures(data);
	checkFloorPriceConfig(data);
	checkManualFloorRules(data);
	checkServiceConfigs(data);
};

const checkRootLevelFeatures = (data) => {
	const { bannerFpConfig, CONFIG_STRUCTURE_KEYS, SERVICE_CONFIG_KEYS, MANUAL_FLOOR_KEYS } = data;
	const siteId = bannerFpConfig[CONFIG_STRUCTURE_KEYS.SITE_ID];
	const siteDomain = bannerFpConfig[CONFIG_STRUCTURE_KEYS.SITE_DOMAIN];
	const floorPriceConfig = bannerFpConfig[CONFIG_STRUCTURE_KEYS.FLOOR_PRICING_CONFIG];
	const serviceConfigs = bannerFpConfig[SERVICE_CONFIG_KEYS.CONFIGS];
	const manualFloorRules = bannerFpConfig[MANUAL_FLOOR_KEYS.MANUAL_FLOOR_RULES_KEY];

	if (typeof siteId !== 'number' || !siteId) {
		throw new Error('siteId is invalid!');
	}
	if (typeof siteDomain !== 'string' || !siteDomain) {
		throw new Error('siteDomain is invalid!');
	}
	if (typeof floorPriceConfig !== 'object') {
		throw new Error('floorPriceConfig is not an object or invalid!');
	}
	// checking if it is defined since configs is optional
	if (
		hasOwnPropertySafe(bannerFpConfig, SERVICE_CONFIG_KEYS.CONFIGS) &&
		!Array.isArray(serviceConfigs)
	) {
		throw new Error('serviceConfigs is not an array!');
	}
	// checking if it is defined since manual Floors is optional
	if (
		hasOwnPropertySafe(bannerFpConfig, MANUAL_FLOOR_KEYS.MANUAL_FLOOR_RULES_KEY) &&
		!Array.isArray(manualFloorRules)
	) {
		throw new Error('manualFloorRules is not an array!');
	}
};

const checkFloorPriceConfig = (data) => {
	const { bannerFpConfig, CONFIG_STRUCTURE_KEYS } = data;
	const floorPriceConfig = bannerFpConfig[CONFIG_STRUCTURE_KEYS.FLOOR_PRICING_CONFIG];

	const enabled = floorPriceConfig[CONFIG_STRUCTURE_KEYS.ENABLED];
	const loggingVersion = floorPriceConfig[CONFIG_STRUCTURE_KEYS.LOGGING_VERSION];
	const trafficSplit = floorPriceConfig[CONFIG_STRUCTURE_KEYS.TRAFFIC_SPLIT];
	const floorEngineTypeSplit = floorPriceConfig[CONFIG_STRUCTURE_KEYS.FLOOR_ENGINE_TYPE_SPLIT];

	if (typeof enabled !== 'boolean') {
		throw new Error('Enabled property is invalid!');
	}
	if (typeof loggingVersion !== 'number') {
		throw new Error('loggingVersion is not a number and is invalid!');
	}
	if (typeof trafficSplit !== 'number') {
		throw new Error('trafficSplit is not a number!');
	}
	if (trafficSplit < 0 || trafficSplit > 1) {
		throw new Error('trafficSplit is out of range!');
	}
	if (!Array.isArray(floorEngineTypeSplit)) {
		throw new Error('FloorEngineTypeSplit is not an array!');
	}

	checkFloorEngineTypeSplit(data);
};

const checkFloorEngineTypeSplit = (data) => {
	const { bannerFpConfig, CONFIG_STRUCTURE_KEYS, ALL_ENGINE_MODELS } = data;
	const floorPriceConfig = bannerFpConfig[CONFIG_STRUCTURE_KEYS.FLOOR_PRICING_CONFIG];
	const floorEngineTypeSplit = floorPriceConfig[CONFIG_STRUCTURE_KEYS.FLOOR_ENGINE_TYPE_SPLIT];

	let trafficSplitSum = 0;
	floorEngineTypeSplit.forEach((floorModel) => {
		const engineType = floorModel[CONFIG_STRUCTURE_KEYS.ENGINE_TYPE];
		const trafficSplit = floorModel[CONFIG_STRUCTURE_KEYS.TRAFFIC_SPLIT];
		trafficSplitSum += trafficSplit;
		//engineType must be of the present engine types
		let engineTypeValid = false;

		ALL_ENGINE_MODELS.forEach((engineModelAvailable) => {
			if (engineModelAvailable === engineType) {
				engineTypeValid = true;
			}
		});

		if (!engineTypeValid) {
			throw new Error('Engine Type is invalid!');
		}

		//trafficSplit is mandatory
		if (typeof trafficSplit !== 'number') {
			throw new Error('Traffic split is not a number!');
		}
		if (trafficSplit < 0 || trafficSplit > 1) {
			throw new Error('traffic split out of range!');
		}
		checkMandatoryEngineKeys(CONFIG_STRUCTURE_KEYS, floorModel);
		validateEngineConfigTypes(floorModel);
	});

	if (trafficSplitSum !== 1) {
		throw new Error('Check total traffic split in floorEngineTypeSplit, seems invalid!');
	}
};

const checkManualFloorRules = (data) => {
	const { bannerFpConfig, MANUAL_FLOOR_KEYS } = data;

	// since this is optional
	if (!hasOwnPropertySafe(bannerFpConfig, MANUAL_FLOOR_KEYS.MANUAL_FLOOR_RULES_KEY)) return;

	const manualFloorRules = bannerFpConfig[MANUAL_FLOOR_KEYS.MANUAL_FLOOR_RULES_KEY];

	if (!Array.isArray(manualFloorRules)) {
		throw new Error('ManualFloorRules should be an array!');
	}

	manualFloorRules.forEach((rule) => {
		if (!Array.isArray(rule.adUnits) || !rule.adUnits.every((item) => typeof item === 'string')) {
			throw new Error('ManualFloorRule "adUnits" should be an array of strings!');
		}

		const uniqueAdUnits = new Set(rule.adUnits);
		if (uniqueAdUnits.size !== rule.adUnits.length) {
			throw new Error('ManualFloorRule "adUnits" contains duplicate values!');
		}

		if (
			!Array.isArray(rule.countries) ||
			!rule.countries.every((item) => typeof item === 'string')
		) {
			throw new Error('ManualFloorRule "countries" should be an array of string!');
		}

		if (
			!Array.isArray(rule.platforms) ||
			!rule.countries.every((item) => typeof item === 'string')
		) {
			throw new Error('ManualFloorRule "platforms" should be an array of string!');
		}

		if (typeof rule.floor !== 'number') {
			throw new Error('ManualFloorRule "floor" should be a number!');
		}
	});
};

const checkServiceConfigs = (data) => {
	const { bannerFpConfig, SERVICE_CONFIG_KEYS, ALL_SERVICES } = data;
	const serviceConfigs = bannerFpConfig[SERVICE_CONFIG_KEYS.CONFIGS];

	if (!hasOwnPropertySafe(bannerFpConfig, SERVICE_CONFIG_KEYS.CONFIGS)) return;

	if (!Array.isArray(serviceConfigs)) {
		throw new Error('ServiceConfigs should be an array!');
	}

	serviceConfigs.forEach((config) => {
		const serviceType = config[SERVICE_CONFIG_KEYS.SERVICE_TYPE];
		if (typeof config[SERVICE_CONFIG_KEYS.ENABLED] !== 'boolean') {
			throw new Error('ServiceConfig "enabled" should be a boolean!');
		}

		if (typeof serviceType !== 'string') {
			throw new Error('ServiceConfig "serviceType" should be a string!');
		}

		//serviceType must be of the present engine types
		let serviceTypeValid = false;

		ALL_SERVICES.forEach((serviceAvailable) => {
			if (serviceAvailable === serviceType) {
				serviceTypeValid = true;
			}
		});

		if (!serviceTypeValid) {
			throw new Error('Service Type is invalid!');
		}

		checkMandatoryServiceKeys(SERVICE_CONFIG_KEYS, config);
		validateServiceConfigTypes(config);
	});
};

const checkMandatoryEngineKeys = (CONFIG_STRUCTURE_KEYS, floorModel) => {
	const engineType = floorModel[CONFIG_STRUCTURE_KEYS.ENGINE_TYPE];
	const mandatoryKeysForEngine = MANDATORY_FP_CONFIG_KEYS[engineType];
	if (!mandatoryKeysForEngine) return;
	mandatoryKeysForEngine.forEach((key) => {
		if (!hasOwnPropertySafe(floorModel, key)) {
			throw new Error(
				`Missing mandatory key in engine: "${key}" for engine type: "${engineType}!"`
			);
		}
	});
};

const checkMandatoryServiceKeys = (SERVICE_CONFIG_KEYS, config) => {
	const serviceType = config[SERVICE_CONFIG_KEYS.SERVICE_TYPE];
	const mandatoryKeysForService = MANDATORY_SERVICE_KEYS[serviceType];
	if (!mandatoryKeysForService) return;
	mandatoryKeysForService.forEach((key) => {
		if (!hasOwnPropertySafe(config, key)) {
			throw new Error(
				`Missing mandatory key in service config: "${key}" for service type: "${serviceType}!"`
			);
		}
	});
};

const validateServiceConfigTypes = (serviceConfig) => {
	for (const [dataType, keysOfType] of Object.entries(SERVICE_KEY_TYPES)) {
		for (const serviceKey of keysOfType) {
			if (hasOwnPropertySafe(serviceConfig, serviceKey)) {
				const actualValue = serviceConfig[serviceKey];
				if (!checkType(actualValue, dataType)) {
					throw new Error(
						`Key "${serviceKey}" expected to be of type "${dataType}", but it's not!`
					);
				}
			}
		}
	}
};

const validateEngineConfigTypes = (floorModelConfig) => {
	for (const [dataType, keysOfType] of Object.entries(ENGINE_KEY_TYPES)) {
		for (const engineKey of keysOfType) {
			if (hasOwnPropertySafe(floorModelConfig, engineKey)) {
				const actualValue = floorModelConfig[engineKey];
				if (!checkType(actualValue, dataType)) {
					throw new Error(`Key "${engineKey}" expected to be of type "${dataType}", but it's not!`);
				}
			}
		}
	}
};
